import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Plus, Phone, Mail, MapPin, Trash2, Upload } from "lucide-react";

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-500/20 dark:text-blue-400 dark:border-blue-500/30",
  contacted: "bg-cyan-100 text-cyan-800 border-cyan-300 dark:bg-cyan-500/20 dark:text-cyan-400 dark:border-cyan-500/30",
  tour_scheduled: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/20 dark:text-amber-400 dark:border-amber-500/30",
  tour_complete: "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-500/20 dark:text-orange-400 dark:border-orange-500/30",
  contract_sent: "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-500/20 dark:text-yellow-400 dark:border-yellow-500/30",
  contract_signed: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-400 dark:border-emerald-500/30",
  paid: "bg-green-100 text-green-800 border-green-300 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/30",
  cancelled: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-500/20 dark:text-slate-400 dark:border-slate-500/30",
  refunded: "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-500/20 dark:text-orange-400 dark:border-orange-500/30",
  lost: "bg-red-100 text-red-800 border-red-300 dark:bg-red-500/20 dark:text-red-400 dark:border-red-500/30",
};

const STATUS_TAB_COLORS = {
  new: "border-blue-500 text-blue-700 dark:text-blue-400",
  contacted: "border-cyan-500 text-cyan-700 dark:text-cyan-400",
  tour_scheduled: "border-amber-500 text-amber-700 dark:text-amber-400",
  tour_complete: "border-orange-500 text-orange-700 dark:text-orange-400",
  contract_sent: "border-yellow-500 text-yellow-700 dark:text-yellow-400",
  contract_signed: "border-emerald-500 text-emerald-700 dark:text-emerald-400",
  paid: "border-green-500 text-green-700 dark:text-green-400",
  cancelled: "border-slate-500 text-slate-700 dark:text-slate-400",
  refunded: "border-orange-500 text-orange-700 dark:text-orange-400",
  lost: "border-red-500 text-red-700 dark:text-red-400",
};

const STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  tour_scheduled: "Tour Scheduled",
  tour_complete: "Tour Complete",
  contract_sent: "Contract Sent",
  contract_signed: "Contract Signed",
  paid: "Paid",
  cancelled: "Cancelled",
  refunded: "Refunded",
  lost: "Lost",
};

const TYPE_LABELS = {
  rental: "Rental",
  birthday_party: "Birthday Party",
  picnic: "Picnic",
  event: "Event",
};

const typeBadgeLabel = (lead) => {
  if (lead.inquiry_type === "rental" || lead.inquiry_type === "field_rental") {
    return lead.rental_subtype === "other" ? "Other Rental" : "Athletic Rental";
  }
  return TYPE_LABELS[lead.inquiry_type] || lead.inquiry_type;
};

const TYPE_BADGE_CLASS = {
  rental: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/30",
  field_rental: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/30",
  birthday_party: "bg-pink-100 text-pink-800 border-pink-300 dark:bg-pink-500/15 dark:text-pink-400 dark:border-pink-500/30",
  picnic: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/30",
  event: "bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-500/15 dark:text-violet-400 dark:border-violet-500/30",
};

export default function LeadsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [passcode, setPasscode] = useState("");

  const loadLeads = () => {
    api.get("/leads")
      .then(({ data }) => setLeads(data.leads))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadLeads(); }, []);

  const statusCounts = useMemo(() => {
    const counts = { all: leads.length };
    for (const s of Object.keys(STATUS_LABELS)) {
      counts[s] = leads.filter((l) => l.status === s).length;
    }
    return counts;
  }, [leads]);

  const filtered = leads.filter((l) => {
    const matchSearch =
      `${l.first_name} ${l.last_name} ${l.email} ${l.phone}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || l.status === statusFilter;
    const matchType =
      typeFilter === "all" ||
      (typeFilter === "rental_athletic" && l.inquiry_type === "rental" && l.rental_subtype !== "other") ||
      (typeFilter === "rental_other" && l.inquiry_type === "rental" && l.rental_subtype === "other") ||
      l.inquiry_type === typeFilter;
    return matchSearch && matchStatus && matchType;
  });

  const handleDelete = async () => {
    if (passcode !== "1234") { toast.error("Invalid passcode"); return; }
    try {
      await api.delete(`/leads/${deleteTarget}`, { data: { passcode: "1234" } });
      toast.success("Lead deleted");
      setLeads((prev) => prev.filter((l) => l.id !== deleteTarget));
    } catch { toast.error("Delete failed"); }
    setDeleteTarget(null);
    setPasscode("");
  };

  return (
    <div className="space-y-4" data-testid="leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="font-outfit text-2xl font-bold tracking-tight">Leads</h1>
        {(user?.role === "super_admin" || user?.role === "company_admin") && (
          <div className="flex gap-2">
            <Button data-testid="import-leads-btn" onClick={() => navigate("/leads/import")} size="sm" variant="outline">
              <Upload size={16} />
              Import CSV
            </Button>
            <Button data-testid="create-lead-btn" onClick={() => navigate("/leads/new")} size="sm">
              <Plus size={16} />
              New Lead
            </Button>
          </div>
        )}
      </div>

      {/* Type filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none" data-testid="type-filter">
        {[
          { key: "all", label: "All Types" },
          { key: "rental_athletic", label: "Athletic Rental" },
          { key: "rental_other", label: "Other Rental" },
          { key: "birthday_party", label: "Birthday" },
          { key: "event", label: "Event" },
          { key: "picnic", label: "Picnic" },
        ].map(({ key, label }) => {
          const count =
            key === "all"
              ? leads.length
              : key === "rental_athletic"
                ? leads.filter((l) => l.inquiry_type === "rental" && l.rental_subtype !== "other").length
                : key === "rental_other"
                  ? leads.filter((l) => l.inquiry_type === "rental" && l.rental_subtype === "other").length
                  : leads.filter((l) => l.inquiry_type === key).length;
          if (key !== "all" && count === 0) return null;
          const active = typeFilter === key;
          return (
            <button
              key={key}
              data-testid={`type-${key}`}
              onClick={() => setTypeFilter(active && key !== "all" ? "all" : key)}
              className={`shrink-0 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                active
                  ? "border-purple-500 text-purple-400 bg-purple-500/10"
                  : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              }`}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {/* Status tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none" data-testid="status-tabs">
        <button
          data-testid="tab-all"
          onClick={() => setStatusFilter("all")}
          className={`shrink-0 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
            statusFilter === "all"
              ? "border-primary bg-primary/10 text-primary"
              : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
          }`}
        >
          All ({statusCounts.all})
        </button>
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          statusCounts[key] > 0 && (
            <button
              key={key}
              data-testid={`tab-${key}`}
              onClick={() => setStatusFilter(statusFilter === key ? "all" : key)}
              className={`shrink-0 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                statusFilter === key
                  ? `${STATUS_TAB_COLORS[key]} border-current bg-current/10`
                  : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              }`}
            >
              {label} ({statusCounts[key]})
            </button>
          )
        ))}
      </div>

      {/* Search + dropdown filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="leads-search"
            placeholder="Search leads..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="leads-status-filter" className="w-full sm:w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v} ({statusCounts[k] || 0})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No leads found
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((lead) => (
            <Card
              key={lead.id}
              data-testid={`lead-card-${lead.id}`}
              className="border-border cursor-pointer hover:-translate-y-0.5 transition-transform"
              onClick={() => navigate(`/leads/${lead.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-foreground">
                        {lead.first_name} {lead.last_name}
                      </span>
                      <Badge className={`text-xs ${STATUS_COLORS[lead.status] || ""}`}>
                        {STATUS_LABELS[lead.status] || lead.status}
                      </Badge>
                      <Badge variant="outline" className={`text-xs ${TYPE_BADGE_CLASS[lead.inquiry_type] || ""}`} data-testid={`lead-type-${lead.id}`}>
                        {typeBadgeLabel(lead)}
                      </Badge>
                      {lead.location && (
                        <Badge variant="outline" className="text-xs bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/30" data-testid={`lead-location-${lead.id}`}>
                          <MapPin size={10} className="mr-1" />
                          {lead.location === "rrdc_field" ? "Rolling River" : "Turf House"}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                      {lead.email && (
                        <span className="flex items-center gap-1">
                          <Mail size={12} /> {lead.email}
                        </span>
                      )}
                      {lead.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={12} /> {lead.phone}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground">
                      {lead.requested_date || "No date"}
                    </span>
                    <Button
                      data-testid={`delete-lead-${lead.id}`}
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-red-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(lead.id);
                      }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete passcode dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => { setDeleteTarget(null); setPasscode(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Lead</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Enter passcode to confirm deletion.</p>
          <Input
            data-testid="delete-passcode-input"
            type="password"
            placeholder="Enter passcode"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleDelete()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setPasscode(""); }}>Cancel</Button>
            <Button data-testid="delete-passcode-confirm" variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
